import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Shield, Eye, EyeOff, CheckCircle2, AlertCircle, Key } from 'lucide-react';
import { Badge } from '../ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Form states
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [mode, setMode] = useState('test');
  const [showSecret, setShowSecret] = useState(false);
  
  // Password verification dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/payment-settings`);
      setSettings(response.data);
      
      if (response.data.configured) {
        setMode(response.data.mode || 'test');
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => {
    // Validate inputs
    if (!keyId || !keySecret) {
      toast.error('Please enter both Key ID and Key Secret');
      return;
    }

    if (!keyId.startsWith('rzp_')) {
      toast.error('Invalid Razorpay Key ID format (should start with rzp_)');
      return;
    }

    // Open password verification dialog
    setShowPasswordDialog(true);
  };

  const handleSaveWithPassword = async () => {
    if (!adminPassword) {
      toast.error('Please enter your password');
      return;
    }

    try {
      setSaving(true);
      
      await axios.put(`${API}/admin/payment-settings`, {
        key_id: keyId,
        key_secret: keySecret,
        mode: mode,
        admin_password: adminPassword
      });

      toast.success('Payment settings updated successfully');
      
      // Reset form
      setKeyId('');
      setKeySecret('');
      setAdminPassword('');
      setShowPasswordDialog(false);
      
      // Refresh settings
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      if (error.response?.status === 403) {
        toast.error('Invalid password');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      await axios.post(`${API}/admin/payment-settings/test`);
      toast.success('Connection successful! Keys are valid.');
    } catch (error) {
      console.error('Connection test failed:', error);
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="flex justify-center">
            <div className="spinner"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Razorpay Payment Gateway Settings
              </CardTitle>
              <CardDescription className="mt-2">
                Configure Razorpay API keys for payment processing. Only super admins can update these settings.
              </CardDescription>
            </div>
            {settings?.configured && (
              <Badge variant="success" className="bg-green-600 text-white">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Configured
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Current Configuration Status */}
          {settings?.configured && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">Current Configuration</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Key ID:</span>
                  <span className="font-mono text-gray-900">{settings.key_id_masked}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Mode:</span>
                  <Badge variant={settings.mode === 'live' ? 'destructive' : 'outline'}>
                    {settings.mode === 'live' ? 'LIVE' : 'TEST'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Last Updated:</span>
                  <span className="text-gray-600">
                    {new Date(settings.updated_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Updated By:</span>
                  <span className="text-gray-600">{settings.updated_by}</span>
                </div>
              </div>
              
              <div className="mt-4">
                <Button
                  onClick={handleTestConnection}
                  disabled={testing}
                  variant="outline"
                  size="sm"
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </Button>
              </div>
            </div>
          )}

          {/* Warning for Live Mode */}
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-1">Important Security Notice</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Your password will be required to save API keys</li>
                <li>Keys are encrypted and stored securely in the database</li>
                <li>Use TEST mode keys for testing, LIVE mode for production</li>
                <li>Never share your API keys with anyone</li>
              </ul>
            </div>
          </div>

          {/* Update Form */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="font-semibold text-lg">Update API Keys</h3>
            
            {/* Mode Selection */}
            <div>
              <Label>Environment Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Test Mode</SelectItem>
                  <SelectItem value="live">Live Mode (Production)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Use Test mode for development and Live mode for production
              </p>
            </div>

            {/* Key ID */}
            <div>
              <Label>Razorpay Key ID *</Label>
              <Input
                type="text"
                value={keyId}
                onChange={(e) => setKeyId(e.target.value)}
                placeholder="rzp_test_xxxxx or rzp_live_xxxxx"
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Get from Razorpay Dashboard → Settings → API Keys
              </p>
            </div>

            {/* Key Secret */}
            <div>
              <Label>Razorpay Key Secret *</Label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={keySecret}
                  onChange={(e) => setKeySecret(e.target.value)}
                  placeholder="Enter your secret key"
                  className="font-mono pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This will be encrypted before storage
              </p>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveClick}
              disabled={!keyId || !keySecret}
              className="w-full"
            >
              <Shield className="w-4 h-4 mr-2" />
              Save Payment Settings (Requires Password)
            </Button>
          </div>

          {/* Documentation Link */}
          <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
            <p className="font-semibold mb-2">Need help getting your API keys?</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Log in to <a href="https://dashboard.razorpay.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Razorpay Dashboard</a></li>
              <li>Go to Settings → API Keys</li>
              <li>Generate or view your existing keys</li>
              <li>Copy Key ID and Key Secret</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Password Verification Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Verify Your Password
            </DialogTitle>
            <DialogDescription>
              For security reasons, please enter your admin password to update payment settings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Admin Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pr-10"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveWithPassword();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
              <p>This ensures that only authorized admins can modify payment gateway settings.</p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowPasswordDialog(false);
                setAdminPassword('');
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveWithPassword}
              disabled={saving || !adminPassword}
              className="flex-1"
            >
              {saving ? 'Saving...' : 'Confirm & Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentSettings;
